// src/services/http.ts
import axios, { AxiosError } from "axios";
import { tokenStore } from "./token";

// ===================== CONFIG =====================
const RAW_API_URL =
  process.env.REACT_APP_API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  "";

// normalize baseURL (bỏ slash cuối)
const API_URL = RAW_API_URL.replace(/\/+$/, "");

/**
 * baseURL ví dụ:
 * - "https://localhost:7215"
 * - "" (nếu proxy dev / relative)
 */
export const http = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

// ✅ boot: nếu đã có token từ trước (reload trang) thì set sẵn Authorization
const bootToken = tokenStore.getAccess();
if (bootToken) {
  http.defaults.headers.common.Authorization = `Bearer ${bootToken}`;
}

// ===================== TOKEN HELPERS (USE tokenStore) =====================
const getAccessToken = () => tokenStore.getAccess();
const getRefreshToken = () => tokenStore.getRefresh();

const setTokens = (accessToken: string, refreshToken?: string) => {
  if (refreshToken) tokenStore.set(accessToken, refreshToken);
  else tokenStore.setAccess(accessToken);

  // ✅ set default header để request sau khỏi thiếu
  http.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};

const clearTokens = () => {
  tokenStore.clear();
  delete http.defaults.headers.common.Authorization;
};

// ===================== REDIRECT TO AUTH (KEEP FROM) =====================
const redirectToAuth = () => {
  const from = window.location.pathname + window.location.search;
  const qs = new URLSearchParams();
  qs.set("tab", "login");
  qs.set("from", from);
  window.location.href = `/auth?${qs.toString()}`;
};

// ===================== HELPERS =====================
const isAuthEndpoint = (url: string) => {
  // url có thể là "/api/auth/login" hoặc "api/auth/login"
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/register") ||
    url.includes("/api/auth/refresh") ||
    url.includes("/api/auth/forgot-password") ||
    url.includes("/api/auth/reset-password")
  );
};

const isAdminEndpoint = (url: string) => url.includes("/api/admin/");

// ===================== REQUEST INTERCEPTOR =====================
http.interceptors.request.use(
  (config) => {
    const url = config.url || "";

    // ✅ auth endpoints không cần Bearer (tránh token rác gây hiểu lầm)
    if (!isAuthEndpoint(url)) {
      const token = getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      } else {
        // nếu tokenStore rỗng thì cũng xoá default header cho chắc
        delete http.defaults.headers.common.Authorization;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ===================== REFRESH TOKEN QUEUE =====================
let isRefreshing = false;

let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (token) p.resolve(token);
    else p.reject(error);
  });
  failedQueue = [];
};

// ===================== RESPONSE INTERCEPTOR =====================
http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // network error
    if (!error.response) return Promise.reject(error);

    const status = error.response.status;
    const reqUrl: string = originalRequest?.url || "";

    // ✅ nếu lỗi từ login/register -> để form xử lý, KHÔNG refresh, KHÔNG redirect
    if (reqUrl.includes("/api/auth/login") || reqUrl.includes("/api/auth/register")) {
      return Promise.reject(error);
    }

    /**
     * ✅ CHỐT BUG CỦA BẠN:
     * Backend đang trả 403 cho /api/admin/* khi access token stale/hết hạn,
     * trong khi interceptor cũ chỉ refresh khi 401.
     *
     * => refresh khi 401 hoặc 403 (nhưng chỉ cho admin endpoints).
     */
    const shouldRefresh =
      status === 401 || (status === 403 && isAdminEndpoint(reqUrl));

    if (!shouldRefresh) return Promise.reject(error);

    // nếu refresh mà vẫn 401/403 => logout luôn
    if (reqUrl.includes("/api/auth/refresh")) {
      clearTokens();
      redirectToAuth();
      return Promise.reject(error);
    }

    // tránh loop vô hạn
    if (originalRequest?._retry) {
      clearTokens();
      redirectToAuth();
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearTokens();
      redirectToAuth();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // đang refresh -> queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(http(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      // dùng axios gốc để tránh interceptor của http
      const res = await axios.post(
        `${API_URL}/api/auth/refresh`,
        { refreshToken }
      );

      const newAccessToken = (res.data as any)?.accessToken;
      const newRefreshToken = (res.data as any)?.refreshToken;

      if (!newAccessToken || !newRefreshToken) {
        throw new Error("Invalid refresh response");
      }

      setTokens(newAccessToken, newRefreshToken);
      processQueue(null, newAccessToken);

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      return http(originalRequest);
    } catch (err) {
      processQueue(err, null);
      clearTokens();
      redirectToAuth();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);
